import { isBadge, isLiftableAction, type LiftableAction } from "odyssey-shared";
import { timeoutSignal } from "../../timeout-signal.ts";
import type { FeedbackAccess } from "../feedback/send.ts";

/**
 * How a GM's page reads one family (ADR-056's amendment of 2026-10-06, decision 13): a GET of `/moderation/family`
 * with the page's current session token and no cookie, the answer read field by field. What the page cannot read (a
 * fetch that rejects, a gateway from before the route, a body that is no JSON or out of shape) is a failed read,
 * never an answer.
 */

/**
 * A ban or a freeze that stands on the family, with whether this GM may end it.
 */
export interface FamilyStanding {
  readonly id: string;
  readonly action: LiftableAction;
  readonly issuer: string;
  readonly at: number;
  readonly until: number | null;
  readonly reason: string;
  readonly mayEnd: boolean;
}

/**
 * One family as the gateway answers it: its name as held, whether it is staff, its true staff badge and whether the
 * person has hidden it (0 and false from a gateway that sends neither), and whether this GM may act on it,
 * its characters by first name with the map of the one in the world, what stands on it, its feedback access and its
 * channel mutes with their ends.
 */
export interface FamilyRead {
  readonly family: string;
  readonly staff: boolean;
  readonly badge: number;
  readonly badgeHidden: boolean;
  readonly actable: boolean;
  readonly characters: readonly { readonly first: string; readonly map: string | null }[];
  readonly standing: readonly FamilyStanding[];
  readonly feedback: FeedbackAccess;
  readonly mutes: readonly { readonly channel: string; readonly until: number }[];
}

/**
 * What came of one read: the family; nobody holds that name; the session ended; the roles no longer grant it; over
 * a limit; a refusal the gateway explained; or a failure the page could not read.
 */
export type FamilyReply = { readonly kind: "ok"; readonly family: FamilyRead } | { readonly kind: "missing" | "session" | "forbidden" | "rate" | "failed" | "unreadable" };

/**
 * One read, by the family's name.
 */
export type FamilyFetch = (familyName: string) => Promise<FamilyReply>;

const REQUEST_TIMEOUT_MS = 10_000;

/**
 * Whether a value is a plain object.
 *
 * @param value The value.
 * @returns True for an object that is not an array or null.
 */
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * Reads a standing row against its shape.
 *
 * @param value The row as sent.
 * @returns The row, or null when a field is missing or of another type.
 */
function standingRow(value: unknown): FamilyStanding | null {
  if (!isRecord(value)) {
    return null;
  }
  const { id, action, issuer, at, until, reason, mayEnd } = value;
  return typeof id === "string" && typeof action === "string" && isLiftableAction(action) && typeof issuer === "string" && typeof at === "number" && (until === null || typeof until === "number") && typeof reason === "string" && typeof mayEnd === "boolean" ? { id, action, issuer, at, until, reason, mayEnd } : null;
}

/**
 * Reads a feedback access against its shape.
 *
 * @param value The access as sent.
 * @returns The access, or null when it is out of shape.
 */
function feedbackOf(value: unknown): FeedbackAccess | null {
  if (!isRecord(value)) {
    return null;
  }
  const { state, until } = value;
  if (state === "open" || state === "off") {
    return { state };
  }
  return state === "paused" && typeof until === "number" ? { state, until } : null;
}

/**
 * Reads an answer against its shape: rows and characters out of shape are dropped, an answer whose own fields are
 * out of place is null.
 *
 * @param body The parsed body.
 * @returns The family, or null.
 */
function familyRead(body: unknown): FamilyRead | null {
  if (!isRecord(body)) {
    return null;
  }
  const { family, staff, actable, characters, standing, feedback, mutes } = body;
  const badge = body["badge"] ?? 0;
  const badgeHidden = body["badgeHidden"] ?? false;
  const access = feedbackOf(feedback);
  if (typeof family !== "string" || typeof staff !== "boolean" || typeof badge !== "number" || !isBadge(badge) || typeof badgeHidden !== "boolean" || typeof actable !== "boolean" || !Array.isArray(characters) || !Array.isArray(standing) || access === null || !Array.isArray(mutes)) {
    return null;
  }
  return {
    family,
    staff,
    badge,
    badgeHidden,
    actable,
    characters: characters.filter((row): row is { first: string; map: string | null } => isRecord(row) && typeof row["first"] === "string" && (row["map"] === null || typeof row["map"] === "string")).map((row) => ({ first: row.first, map: row.map })),
    standing: standing.map(standingRow).filter((row): row is FamilyStanding => row !== null),
    feedback: access,
    mutes: mutes.filter((row): row is { channel: string; until: number } => isRecord(row) && typeof row["channel"] === "string" && typeof row["until"] === "number").map((row) => ({ channel: row.channel, until: row.until })),
  };
}

/**
 * Builds the client.
 *
 * @param options The gateway's `/moderation/family` URL, the page's current session token (null when it holds
 *   none), and the fetch, the page's own unless a test gives another.
 * @returns The read; it never rejects.
 */
export function familyClient(options: { readonly endpoint: string; readonly token: () => string | null; readonly fetch?: (input: string, init?: RequestInit) => Promise<Response> }): FamilyFetch {
  const send = options.fetch ?? ((input: string, init?: RequestInit) => fetch(input, init));
  return async (familyName) => {
    const token = options.token();
    if (token === null || token === "") {
      return { kind: "session" };
    }
    const limit = timeoutSignal(REQUEST_TIMEOUT_MS);
    try {
      const response = await send(`${options.endpoint}?name=${encodeURIComponent(familyName)}`, { method: "GET", headers: { Authorization: `Bearer ${token}` }, credentials: "omit", signal: limit.signal });
      let body: unknown;
      try {
        body = await response.json();
      } catch {
        return { kind: "unreadable" };
      }
      if (response.status === 200) {
        const read = familyRead(body);
        return read === null ? { kind: "unreadable" } : { kind: "ok", family: read };
      }
      if (!isRecord(body) || typeof body["error"] !== "string") {
        return { kind: "unreadable" };
      }
      const kinds: Readonly<Record<number, FamilyReply["kind"]>> = { 401: "session", 403: "forbidden", 404: "missing", 429: "rate" };
      return { kind: (kinds[response.status] ?? "failed") as Exclude<FamilyReply["kind"], "ok"> };
    } catch {
      return { kind: "unreadable" };
    } finally {
      limit.settle();
    }
  };
}
